import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Building2, 
  Target, 
  Activity, 
  Bell, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  Users,
  ShieldCheck,
  GitBranch,
  BookOpen,
  UserPlus,
  FileText,
  CalendarClock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const { user, logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/pipeline', label: 'Pipeline', icon: GitBranch },
    { path: '/accounts', label: 'Accounts', icon: Building2 },
    { path: '/opportunities', label: 'Opportunities', icon: Target },
    { path: '/activities', label: 'Activities', icon: Activity },
    { path: '/nil-resources', label: 'NIL Resources', icon: BookOpen },
    { path: '/availability-reports', label: 'Availability Reports', icon: CalendarClock },
    { path: '/resource-leads', label: 'Resource Leads', icon: UserPlus },
    { path: '/quote-pipeline', label: 'Quote Pipeline', icon: FileText },
    ...(user?.is_admin ? [{ path: '/users', label: 'Users', icon: Users, adminOnly: true }] : [])
  ];

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications`, {
          headers: getAuthHeaders()
        });
        setNotifications(response.data);
        setUnreadCount(response.data.filter(n => !n.is_read).length);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [getAuthHeaders]);

  const markAsRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => 
        prev.map(n => n.id === notifId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white border-r border-[#E2E8F0] shadow-sm
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-[#E2E8F0]">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_6a45feef-1fdd-42ef-b734-4d0c936a20ae/artifacts/rnct6wzk_players-health-lockup-color%20%281%29.png" 
                alt="Players Health" 
                className="h-8 w-auto"
              />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-[#475569] hover:text-[#0F172A]"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium
                      ${isActive 
                        ? 'bg-[#007AFF]/10 text-[#007AFF] border-l-2 border-[#007AFF]' 
                        : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F1F5F9]'
                      }
                    `}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    {item.adminOnly && (
                      <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] text-xs border-0 px-1.5">
                        Admin
                      </Badge>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User Section */}
          <div className="p-4 border-t border-[#E2E8F0]">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border border-[#E2E8F0]">
                <AvatarImage src={user?.avatar_url} alt={user?.name} />
                <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF]">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{user?.name}</p>
                  {user?.is_admin && (
                    <ShieldCheck className="w-4 h-4 text-[#8B5CF6] flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-[#94A3B8] truncate">{user?.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-white border-b border-[#E2E8F0] flex items-center justify-between px-4 lg:px-6 shadow-sm">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-[#475569] hover:text-[#0F172A]"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative text-[#475569] hover:text-[#0F172A]"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF3B30] rounded-full text-xs text-white flex items-center justify-center notification-badge">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-white border-[#E2E8F0] shadow-lg">
                <div className="flex items-center justify-between px-3 py-2 border-b border-[#E2E8F0]">
                  <span className="text-sm font-semibold text-[#0F172A]">Notifications</span>
                  {unreadCount > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs text-[#007AFF] hover:text-[#007AFF]/80"
                      onClick={markAllAsRead}
                    >
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="max-h-80">
                  {notifications.length === 0 ? (
                    <div className="px-3 py-8 text-center text-[#94A3B8] text-sm">
                      No notifications
                    </div>
                  ) : (
                    notifications.slice(0, 10).map((notif) => (
                      <div
                        key={notif.id}
                        className={`px-3 py-3 border-b border-[#E2E8F0] cursor-pointer hover:bg-[#F1F5F9] ${!notif.is_read ? 'bg-[#007AFF]/5' : ''}`}
                        onClick={() => markAsRead(notif.id)}
                      >
                        <p className="text-sm text-[#0F172A] font-medium">{notif.title}</p>
                        <p className="text-xs text-[#475569] mt-1">{notif.message}</p>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 text-[#475569] hover:text-[#0F172A]"
                  data-testid="user-menu-btn"
                >
                  <Avatar className="h-8 w-8 border border-[#E2E8F0]">
                    <AvatarImage src={user?.avatar_url} alt={user?.name} />
                    <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF] text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border-[#E2E8F0] shadow-lg">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-[#0F172A]">{user?.name}</p>
                  <p className="text-xs text-[#94A3B8]">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-[#E2E8F0]" />
                <DropdownMenuItem 
                  className="text-[#FF3B30] hover:text-[#FF3B30] focus:text-[#FF3B30] cursor-pointer"
                  onClick={logout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
